import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  type TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import DemoViewSwitch from '@/components/common/DemoViewSwitch';
import ProUpsellDialog from '@/components/Practice/ProUpsellDialog';
import { NAV_RAIL_WIDTH, navWidthTransitionSx } from '@/config/navigation';
import { ROLES } from '@/config/roles.js';
import { useAuth } from '@/hooks/useAuth';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useServerStatus } from '@/store/serverStatusStore';
import { shouldShowAuthenticatedUpsell } from '@/utils/upsellEligibility';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    '& .MuiToolbar-root': {
      minHeight: '56px',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
}));

// The explicit prop type restores Typography's polymorphic `component`, which
// `styled()` otherwise narrows away: the wordmark is pinned to <p> so
// `variant="h6"` stays a type-scale choice and never emits a stray heading.
const LogoText = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: '1.25rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
  },
}));

const ResponsiveButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  px: 3,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    px: 2,
    minWidth: 'auto',
  },
}));

const UserInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
    '& .MuiTypography-root': {
      display: 'none',
    },
  },
  [theme.breakpoints.down('xs')]: {
    '& .welcome-text': {
      display: 'none',
    },
  },
}));

const UserSummaryContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 999,
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
}));

const RoleGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  pointerEvents: 'none',
}));

const formatRoleLabel = (roleValue: string | undefined): string => {
  if (!roleValue) return 'Student';
  if (roleValue === ROLES.USER) return 'Student';
  const withSpaces = roleValue.replace(/_/g, ' ');
  return withSpaces
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const getRoleChipProps = (
  roleValue: string | undefined
): { color: string; icon: React.ReactElement } => {
  switch (roleValue) {
    case ROLES.SUPER_ADMIN:
      return { color: 'secondary', icon: <SecurityIcon /> };
    case ROLES.ADMIN:
      return { color: 'primary', icon: <AdminPanelSettingsIcon /> };
    case ROLES.TEACHER:
      return { color: 'success', icon: <SchoolIcon /> };
    case ROLES.AFFILIATE:
      return { color: 'warning', icon: <CampaignIcon /> };
    case ROLES.USER:
    default:
      return { color: 'info', icon: <PersonOutlineIcon /> };
  }
};

interface HeaderProps {
  /**
   * Desktop nav is collapsed to the icon rail: offset the wordmark so it
   * sits beside the rail instead of hidden under the full-width drawer.
   */
  navRailActive?: boolean;
}

const Header: React.FC<HeaderProps> = ({ navRailActive = false }) => {
  const { loginWithRedirect, logout } = useAuth0();
  const { user, isAuthenticated, userRole, isRoleLoaded } = useAuth();
  const location = useLocation();
  const isServerDown = useServerStatus();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmall = useMediaQuery(theme.breakpoints.down('xs'));
  const { isFree, loading: entitlementLoading } = useEntitlement();
  const [upsellOpen, setUpsellOpen] = useState(false);
  const showJoinButton =
    isRoleLoaded &&
    !entitlementLoading &&
    shouldShowAuthenticatedUpsell(userRole, isFree);

  const handleLogin = () => {
    if (isServerDown) return;
    loginWithRedirect({
      appState: {
        returnTo: location.pathname + location.search + location.hash,
      },
    });
  };

  const getLoginButtonText = (): string => {
    if (isMobile) return 'Login';
    return 'Log In';
  };

  const getServerButtonText = (): string => {
    if (isMobile) return 'Server Down';
    return 'Server Unavailable';
  };

  const getUserGreeting = (): string => {
    if (isVerySmall) return '';
    if (user?.displayName) return user.displayName as string;
    if (isMobile)
      return (
        (user?.given_name as string) ||
        (user?.name as string | undefined)?.split(' ')[0] ||
        'User'
      );
    return (user?.name as string) || 'User';
  };

  return (
    <>
      <StyledAppBar
        position="fixed"
        className="safe-header"
        sx={{ display: { xs: 'none', sm: 'block' } }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              ml: navRailActive ? `${NAV_RAIL_WIDTH}px` : 0,
              ...navWidthTransitionSx('margin-left'),
            }}
          >
            <LogoText variant="h6" component="p" noWrap>
              DeepThink
            </LogoText>
          </Box>

          <Box>
            {!isAuthenticated && !isServerDown && (
              <ResponsiveButton
                variant="contained"
                color="primary"
                onClick={handleLogin}
                size={isMobile ? 'small' : 'medium'}
              >
                {getLoginButtonText()}
              </ResponsiveButton>
            )}

            {!isAuthenticated && isServerDown && (
              <ResponsiveButton
                variant="contained"
                color="error"
                disabled
                size={isMobile ? 'small' : 'medium'}
              >
                {getServerButtonText()}
              </ResponsiveButton>
            )}

            {isAuthenticated && (
              <UserInfoBox>
                <UserSummaryContainer>
                  {getUserGreeting() && (
                    <Typography
                      variant={isMobile ? 'body2' : 'body1'}
                      sx={{
                        fontWeight: 600,
                        maxWidth: { xs: '120px', sm: '200px', md: '260px' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      className="welcome-text"
                    >
                      Welcome, {getUserGreeting()}
                    </Typography>
                  )}
                  {isRoleLoaded && (
                    <RoleGroup>
                      {getRoleChipProps(userRole).icon}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: 0.2,
                          color:
                            userRole === ROLES.SUPER_ADMIN
                              ? theme.palette.secondary.main
                              : userRole === ROLES.ADMIN
                                ? theme.palette.primary.main
                                : userRole === ROLES.TEACHER
                                  ? theme.palette.success.main
                                  : theme.palette.info.main,
                        }}
                      >
                        {formatRoleLabel(userRole)}
                      </Typography>
                    </RoleGroup>
                  )}
                </UserSummaryContainer>
                <DemoViewSwitch size={isMobile ? 'small' : 'medium'} />
                {showJoinButton && (
                  <ResponsiveButton
                    variant="contained"
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    onClick={() => setUpsellOpen(true)}
                    startIcon={<AutoAwesomeIcon fontSize="small" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 999,
                      px: isMobile ? 1.75 : 2.5,
                      background:
                        'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                      boxShadow: '0 10px 22px rgba(37, 99, 235, 0.28)',
                      ':hover': {
                        filter: 'brightness(1.03)',
                        boxShadow: '0 12px 26px rgba(37, 99, 235, 0.34)',
                      },
                    }}
                  >
                    Join DeepThink
                  </ResponsiveButton>
                )}
                <ResponsiveButton
                  variant="outlined"
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() =>
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    })
                  }
                >
                  {isMobile ? 'Logout' : 'Log Out'}
                </ResponsiveButton>
              </UserInfoBox>
            )}
          </Box>
        </Toolbar>
      </StyledAppBar>
      <ProUpsellDialog
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        skipOverview={true}
        sourceLocation="header_join"
      />
    </>
  );
};

export default Header;
